import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
  NoSymbolIcon,
  MagnifyingGlassIcon,
  BuildingLibraryIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import {
  Button,
  Input,
  Select,
  Badge,
  Modal,
  ModalFooter,
  Card,
  LoadingSpinner,
} from '@/components/common';
import {
  useInsurers,
  useCreateInsurer,
  useUpdateInsurer,
  useDeleteInsurer,
  useActivateInsurer,
  usePauseInsurer,
  useBlockInsurer,
  useInsurerStats,
  useCreateInsurerUser,
} from '@/hooks/useInsurers';
import { useCountryLookup } from '@/hooks/useGeography';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { AssetPickerField } from '@/components/media/AssetPicker';
import { getErrorMessage, getFieldErrors, NON_FIELD_KEY } from '@/services/api';
import { phoneError } from '@/utils/validation';
import type { MediaAsset } from '@/services/assets';
import type { Insurer } from '@/types/admin';

/**
 * The compact, render-ready shape the API returns beside each image FK
 * (`logo_image`, `banner_image`, …). It is not a full `MediaAsset` — it exists
 * so a page can render the picture without a second request — so the picker
 * gets it as a `fallbackUrl` until the user chooses something new.
 */
interface AssetBrief {
  id: string;
  src: string;
  width: number | null;
  height: number | null;
  alt_text: string;
}

/**
 * Fields the insurer API gained with the media library. Declared locally rather
 * than in `types/admin.ts` so this page owns its own contract surface.
 */
type InsurerWithMedia = Insurer & {
  logo?: string | null;
  banner?: string | null;
  banner_mobile?: string | null;
  logo_image?: AssetBrief | null;
  banner_image?: AssetBrief | null;
  banner_mobile_image?: AssetBrief | null;
  logo_display_url?: string;
  network_size?: number | null;
  network_label?: string;
  is_public_profile_enabled?: boolean;
  public_profile_slug?: string;
};

/** The three image slots an insurer has, in the order the form shows them. */
const IMAGE_SLOTS = [
  {
    key: 'logo' as const,
    brief: 'logo_image' as const,
    label: 'Logo',
    hint: 'Shown on cards, the insurer page and search results. SVG or a transparent PNG works best.',
    preview: 'aspect-[3/1]',
  },
  {
    key: 'banner' as const,
    brief: 'banner_image' as const,
    label: 'Banner (desktop)',
    hint: 'Wide artwork across the top of the insurer page. Around 1600x500.',
    preview: 'aspect-[16/5]',
  },
  {
    key: 'banner_mobile' as const,
    brief: 'banner_mobile_image' as const,
    label: 'Banner (mobile)',
    hint: 'Optional. Served below 560px so the desktop crop does not lose its subject.',
    preview: 'aspect-[3/2]',
  },
];

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'blocked', label: 'Blocked' },
];

const categoryOptions = [
  { value: '', label: 'All Categories' },
  { value: 'general', label: 'General Insurance' },
  { value: 'life', label: 'Life Insurance' },
];

const categoryLabels: Record<string, string> = {
  life: 'Life',
  general: 'General',
  health: 'Health',
  composite: 'Composite',
};

/** #385: badge colour per derived API status. */
const API_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  active: 'success',
  configured_untested: 'warning',
  test_failed: 'error',
  not_configured: 'default',
};

const getStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'active':
      return 'success';
    case 'paused':
      return 'warning';
    case 'blocked':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Best-effort Code prefill from the company Name: uppercased, every
 * non-alphanumeric run turned into an underscore, capped at the field's
 * max length. Only used while the user has not typed into Code themselves.
 */
const deriveCodeFromName = (name: string): string =>
  name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .slice(0, 50);

/** Mirrors `Insurer.integration_types` on the server, which is a free-form
 *  JSON list; these are the values the platform actually understands. */
const INTEGRATION_METHODS = [
  { value: 'api', label: 'API' },
  { value: 'portal', label: 'Portal' },
  { value: 'email', label: 'Email' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'manual', label: 'Manual' },
] as const;

export function InsurersListPage() {
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [selectedInsurer, setSelectedInsurer] = useState<Insurer | null>(null);
  const [modalErrors, setModalErrors] = useState<{ email?: string; phone?: string }>({});
  // Server-side validation errors from the last failed submit, keyed by field
  // name (DRF's key) so each message renders under its own input instead of
  // an anonymous toast. `NON_FIELD_KEY` holds anything that isn't about one
  // field (e.g. a duplicate-record rejection); that one still goes to a toast.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Whether the user has typed into Code themselves. While false, Code
  // auto-fills from Name; once true (any manual edit, or an existing record
  // being edited) the auto-fill never overwrites what's there.
  const [codeTouched, setCodeTouched] = useState(false);
  const [createUserForm, setCreateUserForm] = useState<{
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    password: string;
    role: 'ADMIN' | 'MANAGER' | 'STAFF';
  }>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    role: 'ADMIN',
  });
  const [formData, setFormData] = useState<{
    name: string;
    code: string;
    legal_name: string;
    short_name: string;
    email: string;
    phone: string;
    website: string;
    category: 'life' | 'general' | 'health' | 'composite';
    country: string;
    irdai_registration_number: string;
    integration_type: 'api' | 'portal' | 'email' | 'hybrid';
    integration_types: string[];
    logo: string | null;
    banner: string | null;
    banner_mobile: string | null;
    network_size: string;
    network_label: string;
    description: string;
    is_public_profile_enabled: boolean;
    public_profile_slug: string;
  }>({
    name: '',
    code: '',
    legal_name: '',
    short_name: '',
    email: '',
    phone: '',
    website: '',
    category: 'general',
    country: '',
    irdai_registration_number: '',
    integration_type: 'api',
    integration_types: [],
    logo: null,
    banner: null,
    banner_mobile: null,
    network_size: '',
    network_label: '',
    description: '',
    is_public_profile_enabled: false,
    public_profile_slug: '',
  });
  // The asset the user just picked, so the preview updates before a save. Falls
  // back to the brief the API returned for whatever is already stored.
  const [pickedAssets, setPickedAssets] = useState<Record<string, MediaAsset | null>>({});

  const { data, isLoading } = useInsurers({
    page,
    page_size: 10,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    category: categoryFilter || undefined,
  });

  const { data: countries } = useCountryLookup();
  const { data: stats } = useInsurerStats();
  const createMutation = useCreateInsurer();
  const updateMutation = useUpdateInsurer();
  const deleteMutation = useDeleteInsurer();
  const activateMutation = useActivateInsurer();
  const pauseMutation = usePauseInsurer();
  const blockMutation = useBlockInsurer();
  const createUserMutation = useCreateInsurerUser();

  const handleOpenModal = (insurer?: Insurer) => {
    // Editing an existing company already has a real Code, and an existing
    // Add form the user is reopening should not silently start rewriting a
    // value they may have already typed -- only a brand-new Add starts
    // untouched.
    setCodeTouched(Boolean(insurer));
    if (insurer) {
      const withMedia = insurer as InsurerWithMedia;
      setSelectedInsurer(insurer);
      setFormData({
        name: insurer.name,
        code: insurer.code,
        legal_name: insurer.legal_name || '',
        short_name: insurer.short_name || '',
        email: insurer.email || '',
        phone: insurer.phone || '',
        website: insurer.website || '',
        category: insurer.category,
        country: insurer.country,
        irdai_registration_number: insurer.irdai_registration_number || '',
        integration_type: insurer.integration_type,
        integration_types: insurer.integration_types ?? [],
        logo: withMedia.logo || null,
        banner: withMedia.banner || null,
        banner_mobile: withMedia.banner_mobile || null,
        network_size:
          withMedia.network_size === null || withMedia.network_size === undefined
            ? ''
            : String(withMedia.network_size),
        network_label: withMedia.network_label || '',
        description: insurer.description || '',
        is_public_profile_enabled: withMedia.is_public_profile_enabled ?? false,
        public_profile_slug: withMedia.public_profile_slug || '',
      });
    } else {
      setSelectedInsurer(null);
      setFormData({
        name: '',
        code: '',
        legal_name: '',
        short_name: '',
        email: '',
        phone: '',
        website: '',
        category: 'general',
        country: '',
        irdai_registration_number: '',
        integration_type: 'api',
        integration_types: [],
        logo: null,
        banner: null,
        banner_mobile: null,
        network_size: '',
        network_label: '',
        description: '',
        is_public_profile_enabled: false,
        public_profile_slug: '',
      });
    }
    setPickedAssets({});
    setModalErrors({});
    setFieldErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInsurer(null);
    setPickedAssets({});
    setModalErrors({});
    setFieldErrors({});
  };

  /** Drop one field's server error, e.g. once the user edits that field. */
  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async () => {
    const nextErrors: { email?: string; phone?: string } = {};
    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required';
    }
    const phoneMsg = phoneError(formData.phone);
    if (phoneMsg) {
      nextErrors.phone = phoneMsg;
    }
    if (nextErrors.email || nextErrors.phone) {
      setModalErrors(nextErrors);
      return;
    }
    // Publishing without a slug would list the company and 404 its profile
    // link: `PublicInsurerViewSet` looks up by `public_profile_slug`.
    if (formData.is_public_profile_enabled && !formData.public_profile_slug.trim()) {
      toast.error('A public URL slug is required to show this company on the public site');
      return;
    }
    setModalErrors({});
    setFieldErrors({});
    // The API takes bare asset ids for the image FKs and treats "" as "clear
    // it"; network_size is an integer field, so an empty box must be null.
    const payload = {
      ...formData,
      logo: formData.logo || '',
      banner: formData.banner || '',
      banner_mobile: formData.banner_mobile || '',
      network_size: formData.network_size.trim() === '' ? null : Number(formData.network_size),
    };
    try {
      if (selectedInsurer) {
        await updateMutation.mutateAsync({ id: selectedInsurer.id, data: payload });
        toast.success('Company updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Company created successfully');
      }
      handleCloseModal();
    } catch (error) {
      // DRF answers a rejected save with per-field messages (e.g. Code's own
      // "no more than 20 characters"); those render under their input instead
      // of a bare toast that never says which box is wrong. A message with no
      // field to sit under (non_field_errors) still toasts, as does anything
      // that isn't a parseable 400 (network error, 500, ...).
      //
      // A field the server rejects but this form doesn't manage at all --
      // some future serializer field with no state, no input, nothing --
      // also has to toast, or the rejection is written into fieldErrors and
      // shown nowhere. Every field the form DOES manage (`formData`'s own
      // keys, including the image pickers and the public-profile checkbox)
      // must carry its own `error=` binding below; this check is a narrow
      // safety net for the fields outside that set, deliberately derived
      // from `formData` itself rather than a second hand-maintained list, so
      // it cannot silently drift out of sync the way the first version of
      // this fix did (logo/banner/banner_mobile/the checkbox were real
      // fields with no binding and no fallback -- their errors vanished).
      const parsed = getFieldErrors(error);
      const nonField = parsed[NON_FIELD_KEY];
      const hasUnmanagedField = Object.keys(parsed).some(
        (key) => key !== NON_FIELD_KEY && !(key in formData)
      );
      setFieldErrors(parsed);
      if (nonField) {
        toast.error(nonField);
      } else if (Object.keys(parsed).length === 0 || hasUnmanagedField) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedInsurer) return;
    try {
      await deleteMutation.mutateAsync(selectedInsurer.id);
      toast.success('Insurer deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedInsurer(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleOpenCreateUserModal = (insurer: Insurer) => {
    setSelectedInsurer(insurer);
    setCreateUserForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      role: 'ADMIN',
    });
    setIsCreateUserModalOpen(true);
  };

  const handleCreateUserSubmit = async () => {
    if (!selectedInsurer) return;
    if (!createUserForm.first_name || !createUserForm.email || !createUserForm.password) {
      toast.error('First name, email, and password are required');
      return;
    }
    try {
      await createUserMutation.mutateAsync({
        insurerId: selectedInsurer.id,
        data: {
          first_name: createUserForm.first_name,
          last_name: createUserForm.last_name || undefined,
          email: createUserForm.email,
          phone: createUserForm.phone || undefined,
          password: createUserForm.password,
          role: createUserForm.role,
        },
      });
      toast.success('User created and mapped successfully');
      setIsCreateUserModalOpen(false);
      setSelectedInsurer(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleStatusChange = async (insurer: Insurer, action: 'activate' | 'pause' | 'block') => {
    try {
      if (action === 'activate') {
        await activateMutation.mutateAsync(insurer.id);
        toast.success('Insurer Activated');
      } else if (action === 'pause') {
        await pauseMutation.mutateAsync(insurer.id);
        toast.success('Insurer Paused');
      } else {
        await blockMutation.mutateAsync(insurer.id);
        toast.success('Insurer Blocked');
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const insurers = data?.results || [];
  const totalPages = Math.ceil((data?.count || 0) / 10);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Insurance Companies</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage insurance companies and their configurations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/insurers/integration-status">
            <Button variant="outline">Integration status</Button>
          </Link>
          <Button variant="primary" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => handleOpenModal()}>
            Add Company
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card padding="md">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Companies</p>
            <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
          </Card>
          <Card padding="md">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active</p>
            <p className="mt-1 text-2xl font-bold text-green-600 dark:text-green-400">{stats.active}</p>
          </Card>
          <Card padding="md">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Inactive</p>
            <p className="mt-1 text-2xl font-bold text-red-600 dark:text-red-400">{stats.inactive}</p>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card padding="md">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="sm:col-span-2">
            <Input
              placeholder="Search insurers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          />
          <Select
            options={categoryOptions}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          />
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : insurers.length === 0 ? (
          <div className="text-center py-12">
            <BuildingLibraryIcon className="w-12 h-12 mx-auto text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No companies found</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Get started by adding your first company.</p>
            <Button variant="primary" className="mt-4" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => handleOpenModal()}>
              Add Company
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Integration</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {insurers.map((insurer) => (
                  <tr key={insurer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {/* Uploaded logo first, legacy pasted URL as the fallback. */}
                        {(insurer as InsurerWithMedia).logo_display_url || insurer.logo_url ? (
                          <img
                            src={(insurer as InsurerWithMedia).logo_display_url || insurer.logo_url}
                            alt={insurer.name}
                            width={40}
                            height={40}
                            loading="lazy"
                            decoding="async"
                            className="w-10 h-10 rounded-lg object-contain bg-gray-100 dark:bg-gray-700 p-1"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                            <BuildingLibraryIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                          </div>
                        )}
                        <div>
                          <Link
                            to={`/country/insurance-companies/${insurer.id}`}
                            className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            {insurer.name}
                          </Link>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{insurer.code}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {categoryLabels[insurer.category] || insurer.category}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-700 dark:text-gray-300">{insurer.email}</p>
                      {insurer.phone && <p className="text-xs text-gray-500 dark:text-gray-400">{insurer.phone}</p>}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="info">{(insurer.integration_type || 'N/A').toUpperCase()}</Badge>
                      {/* Real state, not the type: every insurer defaults to "api". */}
                      {insurer.api_status && (
                        <div className="mt-1">
                          <Badge variant={API_STATUS_VARIANT[insurer.api_status.state] ?? 'default'} size="sm">
                            API: {insurer.api_status.label}
                          </Badge>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={getStatusVariant(insurer.status)}>
                        {(insurer.status || 'unknown').charAt(0).toUpperCase() + (insurer.status || 'unknown').slice(1)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {insurer.status !== 'active' && (
                          <Button variant="ghost" size="sm" onClick={() => handleStatusChange(insurer, 'activate')} title="Activate">
                            <PlayIcon className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        {insurer.status === 'active' && (
                          <Button variant="ghost" size="sm" onClick={() => handleStatusChange(insurer, 'pause')} title="Pause">
                            <PauseIcon className="w-4 h-4 text-yellow-600" />
                          </Button>
                        )}
                        {insurer.status !== 'blocked' && (
                          <Button variant="ghost" size="sm" onClick={() => handleStatusChange(insurer, 'block')} title="Block">
                            <NoSymbolIcon className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenCreateUserModal(insurer)}
                          title="Create User"
                        >
                          <UserPlusIcon className="w-4 h-4 text-primary-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleOpenModal(insurer)} title="Edit">
                          <PencilIcon className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedInsurer(insurer); setIsDeleteModalOpen(true); }}>
                          <TrashIcon className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages} ({data?.count || 0} results)
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedInsurer ? 'Edit Company' : 'Add Company'}
        size="lg"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Company Name"
            name="name"
            value={formData.name}
            onChange={(e) => {
              const name = e.target.value;
              // Code auto-fills from Name until the user has typed into Code
              // themselves -- never overwrite something they already chose.
              setFormData((prev) => ({
                ...prev,
                name,
                code: codeTouched ? prev.code : deriveCodeFromName(name),
              }));
              clearFieldError('name');
              if (!codeTouched) clearFieldError('code');
            }}
            error={fieldErrors.name}
            required
          />
          <Input
            label="Company Code"
            name="code"
            value={formData.code}
            onChange={(e) => {
              setCodeTouched(true);
              setFormData({ ...formData, code: e.target.value.toUpperCase() });
              clearFieldError('code');
            }}
            required
            placeholder="e.g., HDFC, ICICI"
            helperText="Short unique identifier, e.g. CARE_HEALTH"
            error={fieldErrors.code}
          />
          <Input
            label="Legal Name"
            value={formData.legal_name}
            onChange={(e) => {
              setFormData({ ...formData, legal_name: e.target.value });
              clearFieldError('legal_name');
            }}
            error={fieldErrors.legal_name}
          />
          <Input
            label="Short Name"
            value={formData.short_name}
            onChange={(e) => {
              setFormData({ ...formData, short_name: e.target.value });
              clearFieldError('short_name');
            }}
            error={fieldErrors.short_name}
          />
          <Input
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={(e) => {
              setFormData({ ...formData, email: e.target.value });
              if (modalErrors.email) setModalErrors((prev) => ({ ...prev, email: undefined }));
              clearFieldError('email');
            }}
            error={modalErrors.email || fieldErrors.email}
            required
          />
          <Input
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={(e) => {
              setFormData({ ...formData, phone: e.target.value });
              if (modalErrors.phone) setModalErrors((prev) => ({ ...prev, phone: undefined }));
              clearFieldError('phone');
            }}
            placeholder="10-digit mobile number"
            error={modalErrors.phone || fieldErrors.phone}
            required
          />
          <Input
            label="Website"
            value={formData.website}
            onChange={(e) => {
              setFormData({ ...formData, website: e.target.value });
              clearFieldError('website');
            }}
            placeholder="https://www.example.com"
            error={fieldErrors.website}
          />
          <Input
            label="IRDAI Registration Number"
            value={formData.irdai_registration_number}
            onChange={(e) => {
              setFormData({ ...formData, irdai_registration_number: e.target.value });
              clearFieldError('irdai_registration_number');
            }}
            error={fieldErrors.irdai_registration_number}
          />
          <Select
            label="Category"
            options={categoryOptions.filter((o) => o.value)}
            value={formData.category}
            onChange={(e) => {
              setFormData({ ...formData, category: e.target.value as typeof formData.category });
              clearFieldError('category');
            }}
            error={fieldErrors.category}
          />
          <Select
            label="Country"
            options={(countries || []).map((c: { id: string; name: string }) => ({ value: c.id, label: c.name }))}
            value={formData.country}
            onChange={(e) => {
              setFormData({ ...formData, country: e.target.value });
              clearFieldError('country');
            }}
            error={fieldErrors.country}
            placeholder="Select country"
          />
          <Select
            label="Integration Type"
            options={[
              { value: 'api', label: 'API' },
              { value: 'portal', label: 'Portal' },
              { value: 'email', label: 'Email' },
              { value: 'hybrid', label: 'Hybrid' },
            ]}
            value={formData.integration_type}
            onChange={(e) => {
              setFormData({ ...formData, integration_type: e.target.value as typeof formData.integration_type });
              clearFieldError('integration_type');
            }}
            error={fieldErrors.integration_type}
          />
          {/* The multi-valued companion to Integration Type. The column behind
              it existed in the database with no model field for weeks, which
              made every company create fail; surfacing it here is what stops it
              being invisible again. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Integration Methods
            </label>
            <div className="flex flex-wrap gap-3">
              {INTEGRATION_METHODS.map((method) => (
                <label key={method.value} className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={formData.integration_types.includes(method.value)}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        integration_types: e.target.checked
                          ? [...prev.integration_types, method.value]
                          : prev.integration_types.filter((v) => v !== method.value),
                      }));
                      clearFieldError('integration_types');
                    }}
                  />
                  {method.label}
                </label>
              ))}
            </div>
            {fieldErrors.integration_types && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.integration_types}</p>
            )}
          </div>
          <Input
            label="Network size"
            type="number"
            min={0}
            value={formData.network_size}
            onChange={(e) => {
              setFormData({ ...formData, network_size: e.target.value });
              clearFieldError('network_size');
            }}
            placeholder="e.g. 12500"
            error={fieldErrors.network_size}
          />
          <Input
            label="Network label"
            value={formData.network_label}
            onChange={(e) => {
              setFormData({ ...formData, network_label: e.target.value });
              clearFieldError('network_label');
            }}
            placeholder="e.g. Network hospitals"
            error={fieldErrors.network_label}
          />
        </div>

        {/* U-03. The blurb the broker's insurer header shows under the company
            name. The column has existed since the public directory, but no
            form and no admin field could write it, so all 32 live companies
            carry an empty one and that header had nothing to show. */}
        <div className="mt-4">
          <label
            htmlFor="insurer-description"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Description
          </label>
          <textarea
            id="insurer-description"
            name="description"
            value={formData.description}
            onChange={(e) => {
              setFormData({ ...formData, description: e.target.value });
              clearFieldError('description');
            }}
            rows={3}
            placeholder="A short line about the company, shown under its name."
            className="w-full px-3 py-2 border rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent bg-white dark:bg-gray-700 dark:text-gray-100 border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500"
          />
          {fieldErrors.description && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.description}</p>
          )}
        </div>

        {/* PUBLIC SITE-1. `PublicInsurerViewSet` filters on
            `is_public_profile_enabled`, and nothing in the app could set it —
            so the public Insurance Companies directory was empty for all 30
            live insurers and there was no way to change that. A slug is
            required too: it is the lookup field for the public profile URL. */}
        <div className="mt-6 border-t border-gray-200 pt-5 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Public profile</h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Publishes this company to the public Insurance Companies directory, with its
            public products.
          </p>
          <label className="mt-3 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={formData.is_public_profile_enabled}
              onChange={(e) => {
                setFormData({ ...formData, is_public_profile_enabled: e.target.checked });
                clearFieldError('is_public_profile_enabled');
              }}
            />
            Show on the public site
          </label>
          {fieldErrors.is_public_profile_enabled && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.is_public_profile_enabled}</p>
          )}
          {formData.is_public_profile_enabled && (
            <div className="mt-3 sm:max-w-sm">
              <Input
                label="Public URL slug"
                value={formData.public_profile_slug}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    public_profile_slug: e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]+/g, '-'),
                  });
                  clearFieldError('public_profile_slug');
                }}
                placeholder="acme-general"
                error={
                  (formData.is_public_profile_enabled && !formData.public_profile_slug.trim()
                    ? 'A slug is required to publish — it is the public profile URL.'
                    : undefined) || fieldErrors.public_profile_slug
                }
              />
            </div>
          )}
        </div>

        <div className="mt-6 border-t border-gray-200 pt-5 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Images</h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Uploaded images are served responsively (WebP + srcset) on public pages. They take
            precedence over the legacy Logo URL, which keeps working until you replace it.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {IMAGE_SLOTS.map((slot) => {
              const brief = (selectedInsurer as InsurerWithMedia | null)?.[slot.brief] || null;
              const stored = formData[slot.key];
              return (
                <AssetPickerField
                  key={slot.key}
                  label={slot.label}
                  hint={slot.hint}
                  previewClassName={slot.preview}
                  asset={pickedAssets[slot.key] || null}
                  // Only fall back to the stored brief while it is still the
                  // chosen image — clearing the field must clear the preview.
                  fallbackUrl={!pickedAssets[slot.key] && stored && brief ? brief.src : null}
                  onChange={(asset) => {
                    setPickedAssets((current) => ({ ...current, [slot.key]: asset }));
                    setFormData((current) => ({ ...current, [slot.key]: asset ? asset.id : null }));
                    clearFieldError(slot.key);
                  }}
                  error={fieldErrors[slot.key]}
                />
              );
            })}
          </div>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
          >
            {selectedInsurer ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Create User Modal */}
      <Modal
        isOpen={isCreateUserModalOpen}
        onClose={() => {
          setIsCreateUserModalOpen(false);
          setSelectedInsurer(null);
        }}
        title={`Create User for ${selectedInsurer?.name || 'Insurer'}`}
        size="lg"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="First Name"
            value={createUserForm.first_name}
            onChange={(e) => setCreateUserForm({ ...createUserForm, first_name: e.target.value })}
            required
          />
          <Input
            label="Last Name"
            value={createUserForm.last_name}
            onChange={(e) => setCreateUserForm({ ...createUserForm, last_name: e.target.value })}
          />
          <Input
            label="Email"
            type="email"
            value={createUserForm.email}
            onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
            required
          />
          <Input
            label="Phone"
            value={createUserForm.phone}
            onChange={(e) => setCreateUserForm({ ...createUserForm, phone: e.target.value })}
          />
          <Input
            label="Password"
            type="password"
            value={createUserForm.password}
            onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
            required
          />
          {/* V-01: Country Admin creates only a company's Admin; the server refuses
              Manager and Staff here. The company's Admin adds those from their Team page. */}
          <div data-testid="create-user-role">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Role</p>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">Admin</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Managers and Staff are added by the company&apos;s Admin from their Team page.
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setIsCreateUserModalOpen(false);
              setSelectedInsurer(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateUserSubmit} isLoading={createUserMutation.isPending}>
            Create User
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Company"
        size="sm"
      >
        <p className="text-gray-600 dark:text-gray-400">
          Are you sure you want to delete <strong>{selectedInsurer?.name}</strong>? This action cannot be undone.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={deleteMutation.isPending}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default InsurersListPage;
